// 輪巡（LIVE-05）：設定對話框 + 輪巡中的狀態列（第幾頁、倒數、暫停 / 繼續 / 停止）。
// 攝影機清單依目前格數分頁；版面清單輪流套用各自的格數與格子。設定跟著目前的牆（本機記住），存版面時一起存進 tour 欄。
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { layoutApi, type ServerLayout } from '../../lib/deviceApi';
import type { VmsDevice } from '../../lib/devices';
import { fromServerCells, gridOf } from './LayoutMenu';
import { TOUR_INTERVALS, clampInterval, tourPages, type TourSpec } from './tour';
import { wallOf, type Wall } from './wall';

interface Props {
  wall: Wall;
  cameras: VmsDevice[];
  tour: TourSpec | null;
  onTour(t: TourSpec | null): void;
  /** 換一頁：套用這面牆與格子 */
  onApply(wall: Wall, cells: (string | null)[]): void;
}

export function TourControl({ wall, cameras, tour, onTour, onApply }: Props) {
  const { t } = useTranslation();
  const { visible } = useScope();
  const apis = [...new Set(visible.map((h) => h.endpoints.api))];
  const lists = useQueries({ queries: apis.map((api) => ({ queryKey: ['vms-layouts', api], queryFn: () => layoutApi.list(api), retry: false })) });
  const layouts: ServerLayout[] = lists.flatMap((q) => q.data?.data ?? []);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [idx, setIdx] = useState(0);
  const [nextAt, setNextAt] = useState(0);
  const [, setTick] = useState(0);
  // 對話框的草稿
  const [draft, setDraft] = useState<TourSpec>(() => tour ?? { interval_seconds: 10, mode: 'cameras', cameras: [] });

  const pages = useMemo(() => {
    if (!tour) return [];
    if (tour.mode === 'cameras')
      return tourPages(
        (tour.cameras ?? []).filter((k) => cameras.some((c) => c.key === k)),
        wall.size,
      ).map((cells) => ({ wall, cells }));
    return (tour.layouts ?? [])
      .map((id) => layouts.find((l) => l.layout_id === id))
      .filter((l): l is ServerLayout => !!l)
      .map((l) => ({ wall: l.cols ? wallOf(l.grid, l.cols) : wallOf(gridOf(l.grid)), cells: fromServerCells(l.cells) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, cameras, layouts.length, wall.size, wall.cols]);

  // 開始 / 換頁
  useEffect(() => {
    if (!running || paused || !tour || pages.length === 0) return;
    const p = pages[idx % pages.length];
    onApply(p.wall, p.cells);
    const ms = clampInterval(tour.interval_seconds) * 1000;
    setNextAt(Date.now() + ms);
    const timer = setTimeout(() => setIdx((i) => (i + 1) % pages.length), ms);
    const tick = setInterval(() => setTick((x) => x + 1), 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(tick);
    };
    // onApply 每次 render 都是新的；只在頁 / 狀態變時套用
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, paused, idx, pages.length, tour]);

  const onlineKeys = cameras.filter((c) => c.presence === 'online').map((c) => c.key);
  const start = () => {
    const spec: TourSpec =
      draft.mode === 'cameras'
        ? { interval_seconds: clampInterval(draft.interval_seconds), mode: 'cameras', cameras: draft.cameras?.length ? draft.cameras : onlineKeys }
        : { interval_seconds: clampInterval(draft.interval_seconds), mode: 'layouts', layouts: draft.layouts ?? [] };
    onTour(spec);
    setIdx(0);
    setPaused(false);
    setRunning(true);
    setOpen(false);
  };
  const toggleIn = (list: string[] | undefined, v: string) => (list?.includes(v) ? list.filter((x) => x !== v) : [...(list ?? []), v]);

  return (
    <>
      {running && tour ? (
        <span className="tour-bar" role="status" data-testid="tour-bar">
          {t('tour.running', { i: pages.length ? (idx % pages.length) + 1 : 0, n: pages.length })}
          {!paused && pages.length > 1 && <span className="muted"> · {t('tour.next_in', { s: Math.max(0, Math.ceil((nextAt - Date.now()) / 1000)) })}</span>}
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? t('tour.resume') : t('tour.pause')}
          </button>
          <button className="btn btn-sm" onClick={() => setRunning(false)}>
            {t('tour.stop')}
          </button>
        </span>
      ) : (
        <button
          className="btn"
          onClick={() => {
            setDraft(tour ?? { interval_seconds: 10, mode: 'cameras', cameras: onlineKeys });
            setOpen(true);
          }}
        >
          {t('tour.open')}
        </button>
      )}
      <Dialog open={open} title={t('tour.title')} onClose={() => setOpen(false)} wide>
        <label className="field">
          <span>{t('tour.interval')}</span>
          <select className="input" value={draft.interval_seconds} onChange={(e) => setDraft({ ...draft, interval_seconds: Number(e.target.value) })}>
            {TOUR_INTERVALS.map((s) => (
              <option key={s} value={s}>
                {t('tour.seconds', { n: s })}
              </option>
            ))}
          </select>
        </label>
        <div className="seg" role="group" aria-label={t('tour.mode')}>
          <button
            aria-pressed={draft.mode === 'cameras'}
            onClick={() => setDraft({ ...draft, mode: 'cameras', cameras: draft.cameras?.length ? draft.cameras : onlineKeys })}
          >
            {t('tour.mode_cameras')}
          </button>
          <button aria-pressed={draft.mode === 'layouts'} onClick={() => setDraft({ ...draft, mode: 'layouts' })}>
            {t('tour.mode_layouts')}
          </button>
        </div>
        {draft.mode === 'cameras' ? (
          <div className="tour-pick">
            <p className="muted small">{t('tour.cameras_hint', { size: wall.size, pages: tourPages(draft.cameras ?? [], wall.size).length })}</p>
            {cameras.map((c) => (
              <label key={c.key} className="check">
                <input
                  type="checkbox"
                  checked={draft.cameras?.includes(c.key) ?? false}
                  onChange={() => setDraft({ ...draft, cameras: toggleIn(draft.cameras, c.key) })}
                />
                {c.name} <span className="muted small">· {c.homeName}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="tour-pick">
            {layouts.length === 0 && <p className="muted">{t('tour.no_layouts')}</p>}
            {layouts.map((l) => (
              <label key={l.layout_id} className="check">
                <input
                  type="checkbox"
                  checked={draft.layouts?.includes(l.layout_id) ?? false}
                  onChange={() => setDraft({ ...draft, layouts: toggleIn(draft.layouts, l.layout_id) })}
                />
                {l.name}
              </label>
            ))}
          </div>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            disabled={draft.mode === 'layouts' ? !draft.layouts?.length : !(draft.cameras?.length || onlineKeys.length)}
            onClick={start}
          >
            {t('tour.start')}
          </button>
        </div>
      </Dialog>
    </>
  );
}
